import { formatStringAsCurrency } from "./locale";

export type PriceCalculationMode = "inclusive" | "exclusive";

type PriceDisplayRecord = {
  price_calculation_mode: PriceCalculationMode;
};

type UnitPriceDisplayRecord = PriceDisplayRecord & {
  unit_price: number | null;
  unit_price_excluding_tax_in_yen: number | null;
};

type TradingVolumeDisplayRecord = PriceDisplayRecord & {
  trading_volume_in_yen: number | string | null;
  trading_volume_excluding_tax_in_yen: number | string | null;
};

export type StoragePeriodPriceRecord = {
  bonus_in_yen: number | null;
  bonus_excluding_tax_in_yen: number | null;
};

export type StoragePeriodPriceRecordWithId = StoragePeriodPriceRecord & {
  id: number;
};

export const priceCalculationModeChoices = [
  { id: "inclusive", name: "税込" },
  { id: "exclusive", name: "税別" },
];

export const formatPriceCalculationMode = (record: PriceDisplayRecord | undefined) => {
  const mode = record?.price_calculation_mode;
  return priceCalculationModeChoices.find((choice) => choice.id === mode)?.name ?? "";
};

export const getDisplayUnitPriceValue = (record: UnitPriceDisplayRecord) => {
  if (record.price_calculation_mode === "exclusive") {
    return record.unit_price_excluding_tax_in_yen;
  }

  return record.unit_price;
};

export const getDisplayOfferUnitPriceValue = getDisplayUnitPriceValue;

export const getDisplayTradingVolumeValue = (record: TradingVolumeDisplayRecord) => {
  if (record.price_calculation_mode === "exclusive") {
    return record.trading_volume_excluding_tax_in_yen;
  }

  return record.trading_volume_in_yen;
};

export const getDisplayStoragePeriodBonusValue = (
  record: StoragePeriodPriceRecord | null,
  priceCalculationMode: PriceCalculationMode
) => {
  if (record === null) {
    return null;
  }

  if (priceCalculationMode === "exclusive") {
    return record.bonus_excluding_tax_in_yen;
  }

  return record.bonus_in_yen;
};

export const hasBonusExcludingTaxInYen = (storagePeriod: StoragePeriodPriceRecord): boolean => {
  return storagePeriod.bonus_excluding_tax_in_yen !== null;
};

const formatDisplayPrice = (value: number | string | null) => {
  if (value === null) {
    return "";
  }

  return formatStringAsCurrency(String(value));
};

export const formatDisplayUnitPrice = (record: UnitPriceDisplayRecord | undefined) => {
  if (!record) {
    return "";
  }

  return formatDisplayPrice(getDisplayUnitPriceValue(record));
};

export const formatDisplayOfferUnitPrice = formatDisplayUnitPrice;

export const formatDisplayTradingVolume = (record: TradingVolumeDisplayRecord | undefined) => {
  if (!record) {
    return "";
  }

  return formatDisplayPrice(getDisplayTradingVolumeValue(record));
};

export const formatDisplayStoragePeriodBonus = (
  record: StoragePeriodPriceRecord | null,
  priceCalculationMode: PriceCalculationMode
) => {
  return formatDisplayPrice(getDisplayStoragePeriodBonusValue(record, priceCalculationMode));
};
